import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Typography, useTheme } from "@mui/material";
import { GET_GCP_RESOURCE_BY_CHECK_ID } from "../../../../apis/organization-apis/Infra-apis/InfraApis";
import { useAuditContext } from "../../../../hooks/useAuditContext";
import { useParams, useLocation } from "react-router-dom";
import { GET_USER_DETAILS } from "../../../../apis/dashboard-apis/dashboardDetailApi";
import Role from "../../../../utils/roles";
import InfraEvidenceTabBase from "./shared/InfraEvidenceTabBase";
import ResourceNameCell from "../../../Checks/components/common/ResourceNameCell";
import ResourceStatusCell from "../../../Checks/components/common/ResourceStatusCell";
import DefaultCell from "../../../Checks/components/common/DefaultCell";
import { formatDateTime, formatDateForInput, getDateRangeInUTC } from "../../../../utils/dateUtils";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";

const GCPEvidenceTab = ({ checkDetails, checkCategory, defaultStatusFilter = [] }) => {
  const theme = useTheme();
  const location = useLocation();
  const { auditId } = useParams();
  const audit = location.state?.audit;

  // Extract activeCheckId from checkDetails
  const activeCheckId = checkDetails?.active_check_id
    ? parseInt(checkDetails.active_check_id)
    : null;

  const { data: userData } = useQuery(GET_USER_DETAILS);
  const userDetails = userData?.getAccountDetails?.userDetails || {};
  const isAuditor = userDetails?.roles?.some(
    (role) => role?.role_description === Role.AUDITOR
  );
  const userRole = isAuditor ? Role.AUDITOR : Role.ADMIN;

  const { auditStartDate, auditEndDate } = useAuditContext({
    auditId: audit?.id || auditId,
    userRole,
  });

  // State for selected date (initialized once audit bounds are known)
  const [selectedDate, setSelectedDate] = useState("");
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  // Default: if audit start is still in the future, select audit start; else min(today, audit end)
  useEffect(() => {
    if (!auditEndDate || selectedDate) return;

    const todayStr = formatDateForInput(new Date());
    const endStr = formatDateForInput(auditEndDate);
    const startStr = auditStartDate ? formatDateForInput(auditStartDate) : null;

    let defaultDate;
    if (startStr && startStr > todayStr) {
      defaultDate = startStr;
    } else {
      defaultDate = endStr > todayStr ? todayStr : endStr;
    }
    setSelectedDate(defaultDate);
  }, [auditStartDate, auditEndDate, selectedDate]);

  const { data: gcpResourcesData, loading: gcpResourcesLoading, error: gcpResourcesError } = useQuery(
    GET_GCP_RESOURCE_BY_CHECK_ID,
    {
      variables: {
        check_id: Number(activeCheckId),
        ...(selectedDate
          ? getDateRangeInUTC(selectedDate)
          : auditEndDate
            ? getDateRangeInUTC(formatDateForInput(auditEndDate))
            : {}),
        audit_id: audit?.id || auditId,
      },
      skip: !activeCheckId || !(selectedDate || auditEndDate),
    }
  );

  // Handle errors from GraphQL query
  useEffect(() => {
    if (gcpResourcesError) {
      setSnackbarInfo({
        message: gcpResourcesError.message || "Error fetching GCP resources",
        vertical: "top",
        horizontal: "right",
      });
    }
  }, [gcpResourcesError]);

  const mapResource = React.useCallback((resourcesData) => {
    if (!resourcesData) return [];
    return resourcesData.map((res) => ({
      ...res,
      id: res?.id,
      resourceId: res?.resource_id != null ? String(res.resource_id) : "",
      name: res?.resource_name,
      account: res?.account_id || res?.project_id,
      reason: res?.status_reason || "-",
    }));
  }, []);

  const columns = React.useMemo(() => [
    { label: "Resource Name", field: "name", align: "left", width: "20%" },
    { label: "Resource ID", field: "resourceId", align: "left", width: "15%" },
    { label: "Project ID", field: "account", align: "left", width: "15%" },
    { label: "Execution Time", field: "policy_execution_time", align: "left", width: "15%" },
    { label: "Status", field: "status", align: "center", width: "10%" },
    { label: "Reason", field: "reason", align: "left", width: "25%" },
  ], []);

  const renderCell = React.useCallback((row, column) => {
    switch (column.field) {
      case "name":
        return <ResourceNameCell value={row?.name} />;
      case "policy_execution_time":
        return <DefaultCell value={formatDateTime(row?.policy_execution_time)} />;
      case "status":
        return <ResourceStatusCell status={row?.status} />;
      case "reason":
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.5,
              color: theme.customColors.text.strong,
            }}
          >
            {row.reason || "-"}
          </Typography>
        );
      default:
        return <DefaultCell value={row[column.field]} />;
    }
  }, [theme]);

  return (
    <>
      <InfraEvidenceTabBase
        resourcesData={gcpResourcesData?.getGcpResourceByCheckId || []}
        mapResource={mapResource}
        columns={columns}
        renderCell={renderCell}
        loading={gcpResourcesLoading}
        activeCheckId={activeCheckId}
        checkCategory={checkCategory}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        auditStartDate={auditStartDate}
        auditEndDate={auditEndDate}
        defaultStatusFilter={defaultStatusFilter}
      />
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default GCPEvidenceTab;
